import { useEffect, useRef, useState } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';

import Button from '@/components/Button/Button';

/** Seconds the toast counts down before applying the update on its own. */
const AUTO_UPDATE_SECONDS = 20;
/** How often to ask the server for a new service worker while the app is open. */
const UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000;
/** Fallback delay before forcing a reload if the new worker never takes control. */
const RELOAD_FALLBACK_MS = 4000;

const BUTTON_CLASSES =
  'focus-visible:ring-2 focus-visible:ring-(--accent-primary) focus-visible:ring-offset-1 focus-visible:ring-offset-(--bg-surface-strong) focus-visible:outline-none tablet-hz:h-10 tablet-hz:px-4 tablet-hz:text-sm desktop:h-12 desktop:px-5 desktop:text-base';

/**
 * Service-worker update toast: when a new build is waiting, counts down and
 * applies it automatically. "Later" defers to the next launch. Also re-checks
 * hourly and on return to foreground so a long-lived installed PWA updates.
 */
export default function PwaUpdatePrompt() {
  const registrationRef = useRef<ServiceWorkerRegistration | null>(null);

  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swUrl, registration) {
      registrationRef.current = registration ?? null;
    },
  });

  const [secondsLeft, setSecondsLeft] = useState(AUTO_UPDATE_SECONDS);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    const check = () => {
      // Offline or transient network failure — try again next cycle.
      void registrationRef.current?.update().catch(() => {});
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') check();
    };

    const interval = window.setInterval(check, UPDATE_CHECK_INTERVAL_MS);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  const applyUpdate = () => {
    setUpdating(true);
    // The plugin only reloads when workbox-window flags the SW as an update, which
    // is false on pages loaded without SW control (e.g. after a hard reload).
    navigator.serviceWorker?.addEventListener('controllerchange', () => window.location.reload(), { once: true });
    window.setTimeout(() => window.location.reload(), RELOAD_FALLBACK_MS);
    void updateServiceWorker(true);
  };

  useEffect(() => {
    if (!needRefresh || updating) return;
    setSecondsLeft(AUTO_UPDATE_SECONDS);
    const timer = window.setInterval(() => setSecondsLeft((s) => s - 1), 1000);
    return () => window.clearInterval(timer);
  }, [needRefresh, updating]);

  useEffect(() => {
    if (needRefresh && !updating && secondsLeft <= 0) {
      applyUpdate();
    }
  }, [needRefresh, updating, secondsLeft]);

  if (!needRefresh) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="tablet-hz:max-w-lg tablet-hz:gap-4 tablet-hz:p-6 desktop:bottom-[calc(3rem+env(safe-area-inset-bottom))] desktop:max-w-xl desktop:gap-5 desktop:p-8 fixed inset-x-4 bottom-[calc(1rem+env(safe-area-inset-bottom))] z-50 mx-auto flex max-w-md flex-col gap-3 rounded-2xl border border-(--border-default) bg-(--bg-surface-strong) p-4 shadow-(--shadow-modal) backdrop-blur sm:inset-x-6 sm:bottom-[calc(2rem+env(safe-area-inset-bottom))]"
    >
      <div className="tablet-hz:space-y-1">
        <p className="tablet-hz:text-lg desktop:text-xl font-medium text-(--text-primary)">New version available</p>
        <p className="desktop:text-base text-sm text-(--text-secondary)">
          {updating ? 'Updating…' : `Updating automatically in ${Math.max(secondsLeft, 0)}s…`}
        </p>
      </div>
      <div className="tablet-hz:gap-3 flex items-center justify-end gap-2">
        <Button variant="secondary" size="sm" className={BUTTON_CLASSES} disabled={updating} onClick={() => setNeedRefresh(false)}>
          Later
        </Button>
        <Button variant="primary" size="sm" className={BUTTON_CLASSES} disabled={updating} onClick={applyUpdate}>
          Update now
        </Button>
      </div>
    </div>
  );
}
